import React from "react";
import {
  Box,
  TextField,
  Button,
  Paper,
  Typography,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
  Collapse,
  IconButton,
  Chip,
} from "@mui/material";
import { TreeView, TreeItem } from "@mui/lab";
import { LoadingButton } from "@mui/lab";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { PermissionsManager } from "../../components/PermissionsManager/PermissionsManager";
import { configService } from "../../services/config.service";
import { motion } from "framer-motion";
import { PermissionTreeView } from "../../components/PermissionsManager/PermissionTreeView/PermissionTreeView";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

interface Permission {
  id: string;
  name: string;
  description: string;
  subgroup?: string;
}

interface PermissionGroup {
  id: string;
  name: string;
  description: string;
  icon: string;
  permissions: Permission[];
  subgroups?: PermissionGroup[];
  isSubgroup?: boolean;
}

interface BackendPermission {
  _id: string;
  name: string;
}

interface Role {
  _id?: string;
  name: string;
  permissions: string[];
  description?: string;
  is_system_role?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

interface ValidationErrors {
  [key: string]: string;
}

interface PermissionMatrixItem {
  resource: string;
  view?: string;
  add?: string;
  update?: string;
  delete?: string;
  icon?: string;
  description?: string;
  [key: string]: string | undefined; // Index signature for dynamic access
}

interface PermissionMatrix {
  [key: string]: PermissionMatrixItem;
}

const moduleHierarchy: Record<string, string> = {
  raw_materials: "stock-management",
  consumables: "stock-management",
  recipes: "menu",
  sale_items: "menu",
  offers: "menu",
  bom: "menu",
  branches: "facilities",
  warehouses: "facilities",
  roles: "configuration",
};

type RouteKey =
  | "dashboard"
  | "staff"
  | "stock-management"
  | "menu"
  | "suppliers"
  | "facilities"
  | "branch"
  | "warehouse"
  | "pos"
  | "cashiers"
  | "customers"
  | "settings"
  | "configuration"
  | "role";

const routeOrder: Record<RouteKey, number> = {
  dashboard: 1,
  staff: 2,
  "stock-management": 3,
  menu: 4,
  suppliers: 5,
  facilities: 6,
  branch: 7,
  warehouse: 8,
  pos: 9,
  cashiers: 10,
  customers: 11,
  settings: 12,
  configuration: 13,
  role: 14,
};

const isRouteKey = (key: string): key is RouteKey => {
  return key in routeOrder;
};

const getGroupIcon = (groupName: string): string => {
  const icons: Record<string, string> = {
    dashboard: "dashboard",
    staff: "people",
    "stock-management": "inventory_2",
    raw_materials: "inventory",
    consumables: "category",
    menu: "restaurant_menu",
    recipes: "menu_book",
    sale_items: "fastfood",
    offers: "local_offer",
    suppliers: "handshake",
    facilities: "store",
    branches: "store",
    warehouses: "warehouse",
    pos: "point_of_sale",
    cashiers: "payments",
    customers: "people",
    settings: "settings",
    configuration: "admin_panel_settings",
    roles: "security",
  };
  return icons[groupName] || "extension";
};

const getGroupDescription = (groupName: string): string => {
  const descriptions: Record<string, string> = {
    dashboard: "Access to dashboard analytics and overview",
    staff: "Manage staff members and their access",
    "stock-management": "Manage inventory, raw materials, and consumables",
    raw_materials: "Manage raw materials and ingredients",
    consumables: "Manage consumable items and supplies",
    menu: "Manage menu items, recipes, and offers",
    recipes: "Create and manage kitchen recipes",
    sale_items: "Manage menu items for sale",
    offers: "Create and manage promotional offers",
    suppliers: "Manage suppliers and procurement",
    facilities: "Manage branches and warehouses",
    branches: "Manage business branches",
    warehouses: "Manage storage warehouses",
    pos: "Point of sale operations",
    cashiers: "Manage cashier operations",
    customers: "Customer management and loyalty",
    settings: "System settings and preferences",
    configuration: "System configuration and role management",
    roles: "Manage user roles and permissions",
  };
  return descriptions[groupName] || `Manage ${groupName} module`;
};

// Helper function to sort permissions within a group or subgroup
const sortPermissions = (permissions: Permission[]): Permission[] => {
  const actionOrder = { view: 1, add: 2, update: 3, delete: 4 };
  return permissions.sort((a, b) => {
    const actionA = a.name.split(".")[1];
    const actionB = b.name.split(".")[1];
    return (
      (actionOrder[actionA as keyof typeof actionOrder] || 99) -
      (actionOrder[actionB as keyof typeof actionOrder] || 99)
    );
  });
};

const organizePermissionsMatrix = (
  permissions: BackendPermission[]
): PermissionMatrix => {
  const matrix: PermissionMatrix = {};

  permissions.forEach((permission) => {
    const [resource, action] = permission.name.split(".");
    const formattedResource = resource
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

    if (!matrix[resource]) {
      matrix[resource] = {
        resource: formattedResource,
        icon: getGroupIcon(resource),
        description: getGroupDescription(resource),
      };
    }

    matrix[resource][action] = permission._id;
  });

  return matrix;
};

const convertMatrixToGroups = (matrix: PermissionMatrix): PermissionGroup[] => {
  return Object.entries(matrix).map(([key, item]) => ({
    id: key,
    name: item.resource,
    description: item.description || "",
    icon: item.icon || "folder",
    permissions: ["view", "add", "update", "delete"]
      .filter((action) => item[action])
      .map((action) => ({
        id: item[action]!,
        name: `${action.charAt(0).toUpperCase() + action.slice(1)} ${
          item.resource
        }`,
        description: `Permission to ${action} ${item.resource.toLowerCase()}`,
      })),
  }));
};

const PermissionRow: React.FC<{
  permission: Permission;
  selected: boolean;
  onToggle: (id: string) => void;
}> = ({ permission, selected, onToggle }) => (
  <TableRow
    sx={{
      "&:hover": {
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
      },
      cursor: "pointer",
    }}
    onClick={() => onToggle(permission.id)}
  >
    <TableCell padding="checkbox">
      <Checkbox
        checked={selected}
        sx={{
          color: selected ? "#0891B2" : undefined,
          "&.Mui-checked": {
            color: "#0891B2",
          },
        }}
      />
    </TableCell>
    <TableCell>
      <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400 }}>
        {permission.name}
      </Typography>
      {permission.description && (
        <Typography variant="caption" color="text.secondary" display="block">
          {permission.description}
        </Typography>
      )}
    </TableCell>
  </TableRow>
);

const PermissionGroupRow: React.FC<{
  group: PermissionGroup;
  selectedPermissions: string[];
  onTogglePermission: (id: string) => void;
}> = ({ group, selectedPermissions, onTogglePermission }) => {
  const [open, setOpen] = React.useState(true);
  const allSelected = group.permissions.every((p) =>
    selectedPermissions.includes(p.id)
  );
  const someSelected = group.permissions.some((p) =>
    selectedPermissions.includes(p.id)
  );

  const handleGroupToggle = () => {
    if (allSelected) {
      // Deselect all permissions in group
      group.permissions.forEach((p) => onTogglePermission(p.id));
    } else {
      // Select all unselected permissions in group
      group.permissions
        .filter((p) => !selectedPermissions.includes(p.id))
        .forEach((p) => onTogglePermission(p.id));
    }
  };

  return (
    <>
      <TableRow
        sx={{
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
          },
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={allSelected}
            indeterminate={!allSelected && someSelected}
            onChange={handleGroupToggle}
            sx={{
              color: "#0891B2",
              "&.Mui-checked": {
                color: "#0891B2",
              },
              "&.MuiCheckbox-indeterminate": {
                color: "#0891B2",
              },
            }}
          />
        </TableCell>
        <TableCell
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
          }}
          onClick={() => setOpen(!open)}
        >
          <IconButton size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          <Icon sx={{ color: "#0891B2", mr: 1 }}>{group.icon}</Icon>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {group.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {group.permissions.length} permissions available
            </Typography>
          </Box>
          {someSelected && (
            <Chip
              size="small"
              label={`${
                group.permissions.filter((p) =>
                  selectedPermissions.includes(p.id)
                ).length
              } selected`}
              sx={{
                ml: 2,
                bgcolor: alpha("#0891B2", 0.1),
                color: "#0891B2",
                fontWeight: 500,
              }}
            />
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={2}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ py: 1 }}>
              <Table size="small">
                <TableBody>
                  {group.permissions.map((permission) => (
                    <PermissionRow
                      key={permission.id}
                      permission={permission}
                      selected={selectedPermissions.includes(permission.id)}
                      onToggle={onTogglePermission}
                    />
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const RoleBuilder: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = React.useState(false);
  const [saveLoading, setSaveLoading] = React.useState(false);
  const [roleData, setRoleData] = React.useState<Role>({
    name: "",
    permissions: [],
  });
  const [errors, setErrors] = React.useState<ValidationErrors>({});
  const [permissionMatrix, setPermissionMatrix] =
    React.useState<PermissionMatrix>({});
  const [showConfirmation, setShowConfirmation] = React.useState(false);

  React.useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const permissionsResponse = await configService.listPermissions({
        pageSize: 10000,
        pageIndex: 0,
      });

      if (!permissionsResponse?.data) {
        throw new Error("Invalid permissions data received");
      }

      const matrix = organizePermissionsMatrix(permissionsResponse.data);
      setPermissionMatrix(matrix);

      if (id) {
        const roleResponse = await configService.getRole(id);
        const roleData = roleResponse?.data?.data;

        if (!roleData) {
          throw new Error("Role not found");
        }

        setRoleData({
          _id: roleData._id || id,
          name: roleData.name || "",
          permissions: Array.isArray(roleData.permissions)
            ? roleData.permissions
            : [],
          description: roleData.description || "",
          is_system_role: !!roleData.is_system_role,
          created_at: roleData.created_at,
          updated_at: roleData.updated_at,
          created_by: roleData.created_by,
          updated_by: roleData.updated_by,
        });
      }
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to load role data"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setRoleData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handlePermissionsChange = (permissions: string[]) => {
    if (!Array.isArray(permissions)) {
      console.error("Invalid permissions format");
      return;
    }
    setRoleData((prev) => ({ ...prev, permissions }));
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!roleData.name?.trim()) {
      newErrors.name = "Role name is required";
    }

    if (
      !Array.isArray(roleData.permissions) ||
      roleData.permissions.length === 0
    ) {
      newErrors.permissions = "At least one permission must be selected";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setShowConfirmation(true);
  };

  const handleConfirmSave = async () => {
    try {
      setSaveLoading(true);
      const roleToSave = {
        ...roleData,
        name: roleData.name.trim(),
        description: roleData.description?.trim() || "",
        is_system_role: roleData.is_system_role || false,
        permissions: Array.isArray(roleData.permissions)
          ? roleData.permissions
          : [],
      };

      if (id) {
        await configService.updateRole(roleToSave);
        toast.success("Role updated successfully");
      } else {
        await configService.createRole(roleToSave);
        toast.success("Role created successfully");
      }
      navigate("/business/configuration/roles");
    } catch (error) {
      console.error("Error saving role:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to save role"
      );
    } finally {
      setSaveLoading(false);
      setShowConfirmation(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={id ? "Edit Role" : "Create Role"}
        subtitle="Define role and assign permissions"
        icon="admin_panel_settings"
        backButton
        onBack={() => navigate("/business/configuration/roles")}
      />

      {loading ? (
        <Paper sx={{ p: 3 }}>
          <Skeleton height={60} sx={{ mb: 3 }} />
          <Skeleton height={40} sx={{ mb: 2 }} />
          <Skeleton height={400} />
        </Paper>
      ) : (
        <>
          <Paper
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: "1px solid",
              borderColor: (theme) => alpha("#0891B2", 0.1),
            }}
          >
            <Box sx={{ maxWidth: 400, mb: 4 }}>
              <TextField
                fullWidth
                label="Role Name"
                name="name"
                value={roleData.name || ""}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                sx={{ mb: 2 }}
              />
            </Box>

            <Typography
              variant="h6"
              sx={{
                mb: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Icon sx={{ color: "#0891B2" }}>verified</Icon>
              Permissions
            </Typography>

            {errors.permissions && (
              <Typography
                color="error"
                variant="caption"
                sx={{ mb: 2, display: "block" }}
              >
                {errors.permissions}
              </Typography>
            )}

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "300px" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600 }}
                        >
                          Resource
                        </Typography>
                        <Tooltip title="Select a resource to manage all its permissions">
                          <Icon sx={{ color: "text.secondary", fontSize: 16 }}>
                            help_outline
                          </Icon>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ width: "100px" }}>
                      View
                    </TableCell>
                    <TableCell align="center" sx={{ width: "100px" }}>
                      Add
                    </TableCell>
                    <TableCell align="center" sx={{ width: "100px" }}>
                      Update
                    </TableCell>
                    <TableCell align="center" sx={{ width: "100px" }}>
                      Delete
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(permissionMatrix)
                    .sort(([a], [b]) => {
                      const orderA = routeOrder[a as RouteKey] || 99;
                      const orderB = routeOrder[b as RouteKey] || 99;
                      return orderA - orderB;
                    })
                    .map(([key, resource]) => {
                      // Calculate if all available permissions for this resource are selected
                      const availablePermissions = [
                        "view",
                        "add",
                        "update",
                        "delete",
                      ]
                        .filter((action) => resource[action])
                        .map((action) => resource[action]!);
                      const selectedCount = availablePermissions.filter((p) =>
                        roleData.permissions.includes(p)
                      ).length;
                      const allSelected =
                        selectedCount === availablePermissions.length;
                      const someSelected = selectedCount > 0;

                      return (
                        <TableRow
                          key={key}
                          sx={{
                            "&:hover": {
                              bgcolor: (theme) =>
                                alpha(theme.palette.primary.main, 0.04),
                            },
                          }}
                        >
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              <Checkbox
                                checked={allSelected}
                                indeterminate={!allSelected && someSelected}
                                onChange={() => {
                                  const newPermissions = allSelected
                                    ? roleData.permissions.filter(
                                        (p) => !availablePermissions.includes(p)
                                      )
                                    : [
                                        ...new Set([
                                          ...roleData.permissions,
                                          ...availablePermissions,
                                        ]),
                                      ];
                                  handlePermissionsChange(newPermissions);
                                }}
                                sx={{
                                  color: "#0891B2",
                                  "&.Mui-checked": {
                                    color: "#0891B2",
                                  },
                                  "&.MuiCheckbox-indeterminate": {
                                    color: "#0891B2",
                                  },
                                }}
                              />
                              <Icon sx={{ color: "#0891B2" }}>
                                {resource.icon}
                              </Icon>
                              <Box>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {resource.resource}
                                </Typography>
                                {resource.description && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {resource.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </TableCell>
                          {["view", "add", "update", "delete"].map((action) => (
                            <TableCell key={action} align="center">
                              {resource[action] ? (
                                <Checkbox
                                  checked={roleData.permissions.includes(
                                    resource[action]!
                                  )}
                                  onChange={() => {
                                    const permissionId = resource[action]!;
                                    const newPermissions =
                                      roleData.permissions.includes(
                                        permissionId
                                      )
                                        ? roleData.permissions.filter(
                                            (p) => p !== permissionId
                                          )
                                        : [
                                            ...roleData.permissions,
                                            permissionId,
                                          ];
                                    handlePermissionsChange(newPermissions);
                                  }}
                                  sx={{
                                    color: "#0891B2",
                                    "&.Mui-checked": {
                                      color: "#0891B2",
                                    },
                                  }}
                                />
                              ) : (
                                <Icon
                                  sx={{ color: "text.disabled", fontSize: 20 }}
                                >
                                  remove
                                </Icon>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                mt: 3,
                pt: 3,
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                borderTop: "1px solid",
                borderColor: (theme) => alpha("#0891B2", 0.1),
              }}
            >
              <Button
                variant="outlined"
                onClick={() => navigate("/business/configuration/roles")}
                sx={{
                  borderColor: "#0891B2",
                  color: "#0891B2",
                  "&:hover": {
                    borderColor: "#0E7490",
                    bgcolor: alpha("#0891B2", 0.04),
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={
                  !roleData.name.trim() || roleData.permissions.length === 0
                }
                sx={{
                  bgcolor: "#0891B2",
                  "&:hover": {
                    bgcolor: "#0E7490",
                  },
                  "&.Mui-disabled": {
                    bgcolor: alpha("#0891B2", 0.4),
                  },
                }}
              >
                {id ? "Update Role" : "Create Role"}
              </Button>
            </Box>
          </Paper>

          {/* Confirmation Dialog */}
          <Dialog
            open={showConfirmation}
            onClose={() => !saveLoading && setShowConfirmation(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                width: 600,
                maxWidth: "100%",
                borderRadius: 2,
                overflow: "hidden",
                borderLeft: "1px solid",
                borderColor: alpha("#0891B2", 0.1),
              },
            }}
          >
            <Box
              sx={{
                px: 3,
                py: 2,
                background: "#0891B2",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(255,255,255,0.1)",
                  }}
                >
                  <Icon>admin_panel_settings</Icon>
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Confirm Role Details
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Review and confirm role configuration
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => !saveLoading && setShowConfirmation(false)}
                size="small"
                disabled={saveLoading}
                sx={{
                  color: "white",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Icon>close</Icon>
              </IconButton>
            </Box>

            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  bgcolor: alpha("#0891B2", 0.04),
                  border: "1px solid",
                  borderColor: alpha("#0891B2", 0.1),
                  mb: 3,
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {roleData.name || "Unnamed Role"}
                  </Typography>
                </Box>
                {roleData.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.5 }}
                  >
                    {roleData.description}
                  </Typography>
                )}
              </Box>

              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Icon sx={{ color: "text.secondary", fontSize: 20 }}>
                    verified
                  </Icon>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "text.primary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      fontSize: "0.75rem",
                    }}
                  >
                    Selected Permissions ({roleData.permissions?.length || 0})
                  </Typography>
                </Box>

                <Box
                  sx={{
                    border: "1px solid",
                    borderColor: alpha("#0891B2", 0.1),
                    borderRadius: 1,
                    overflow: "hidden",
                  }}
                >
                  <TableContainer sx={{ maxHeight: 300 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "300px" }}>
                            Resource
                          </TableCell>
                          <TableCell align="center" sx={{ width: "100px" }}>
                            View
                          </TableCell>
                          <TableCell align="center" sx={{ width: "100px" }}>
                            Add
                          </TableCell>
                          <TableCell align="center" sx={{ width: "100px" }}>
                            Update
                          </TableCell>
                          <TableCell align="center" sx={{ width: "100px" }}>
                            Delete
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {Object.entries(permissionMatrix)
                          .sort(([a], [b]) => {
                            const orderA = routeOrder[a as RouteKey] || 99;
                            const orderB = routeOrder[b as RouteKey] || 99;
                            return orderA - orderB;
                          })
                          .filter(([_, resource]) =>
                            ["view", "add", "update", "delete"].some(
                              (action) =>
                                resource[action] &&
                                roleData.permissions.includes(resource[action]!)
                            )
                          )
                          .map(([key, resource]) => (
                            <TableRow key={key}>
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Icon sx={{ color: "#0891B2" }}>
                                    {resource.icon}
                                  </Icon>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                  >
                                    {resource.resource}
                                  </Typography>
                                </Box>
                              </TableCell>
                              {["view", "add", "update", "delete"].map(
                                (action) => (
                                  <TableCell key={action} align="center">
                                    {resource[action] &&
                                    roleData.permissions.includes(
                                      resource[action]!
                                    ) ? (
                                      <Icon
                                        sx={{ color: "#059669", fontSize: 18 }}
                                      >
                                        check_circle
                                      </Icon>
                                    ) : (
                                      <Icon
                                        sx={{
                                          color: "text.disabled",
                                          fontSize: 18,
                                        }}
                                      >
                                        remove
                                      </Icon>
                                    )}
                                  </TableCell>
                                )
                              )}
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Box>

              <Box
                sx={{
                  mt: 3,
                  pt: 3,
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 2,
                  borderTop: "1px solid",
                  borderColor: alpha("#0891B2", 0.1),
                }}
              >
                <Button
                  variant="outlined"
                  onClick={() => setShowConfirmation(false)}
                  disabled={saveLoading}
                  sx={{
                    borderColor: "#0891B2",
                    color: "#0891B2",
                    "&:hover": {
                      borderColor: "#0E7490",
                      bgcolor: alpha("#0891B2", 0.04),
                    },
                  }}
                >
                  Back
                </Button>
                <LoadingButton
                  variant="contained"
                  onClick={handleConfirmSave}
                  loading={saveLoading}
                  sx={{
                    bgcolor: "#0891B2",
                    "&:hover": {
                      bgcolor: "#0E7490",
                    },
                  }}
                >
                  Confirm & Save
                </LoadingButton>
              </Box>
            </Box>
          </Dialog>
        </>
      )}
    </Box>
  );
};
